/**
 * Small, pure helpers over a product's pricing model. No engine or state
 * logic lives here — just "is this product priced", "what does its rate
 * mean", and "how does a rep-supplied retail price attach to it".
 */

import type { PricingModel, Product } from './types.js';

/**
 * A product is "priced" when its model's retail field is set. Nothing else
 * substitutes: a missing rate is null, never 0 or a default, and this is the
 * single place that maps model -> "which field must be non-null".
 */
export function isPriced(product: Product): boolean {
  const pricing = product.pricing;
  switch (pricing.model) {
    case 'cpm':
      return pricing.retailCpm != null;
    case 'cpc':
      return pricing.retailCpc != null;
    case 'cpv':
      return pricing.retailCpv != null;
    case 'flat':
      return pricing.flatMonthly != null;
    case 'managed':
      return pricing.retailFeePct != null;
    case 'scoped':
      // A scoped product (e.g. SEO, web development) is only ever priced by
      // a rep override, which sets flatMonthly — same field flat itself uses.
      return pricing.flatMonthly != null;
    default: {
      const never: never = pricing.model;
      throw new Error(`Unknown pricing model: ${String(never)}`);
    }
  }
}

/** Human label for a model's rate, e.g. for "Enter a CPM to price it." */
export function priceUnitLabel(model: PricingModel): string {
  switch (model) {
    case 'cpm':
      return 'CPM';
    case 'cpc':
      return 'per click';
    case 'cpv':
      return 'per view';
    case 'flat':
      return 'per month';
    case 'managed':
      return '% of media spend';
    case 'scoped':
      return 'per month (quoted)';
    default: {
      const never: never = model;
      throw new Error(`Unknown pricing model: ${String(never)}`);
    }
  }
}

/**
 * Returns a copy of `product` with a rep-supplied retail price applied to
 * whichever field its model prices from. For `managed`, `retail` is a
 * fraction (0..1), not a percent. Never mutates the input.
 */
export function applyPriceOverride(product: Product, retail: number): Product {
  const pricing = { ...product.pricing };
  switch (pricing.model) {
    case 'cpm':
      pricing.retailCpm = retail;
      break;
    case 'cpc':
      pricing.retailCpc = retail;
      break;
    case 'cpv':
      pricing.retailCpv = retail;
      break;
    case 'flat':
      pricing.flatMonthly = retail;
      break;
    case 'managed':
      pricing.retailFeePct = retail;
      break;
    case 'scoped':
      pricing.flatMonthly = retail;
      break;
    default: {
      const never: never = pricing.model;
      throw new Error(`Unknown pricing model: ${String(never)}`);
    }
  }
  return { ...product, pricing };
}

/**
 * Share of what the client pays that we keep, 0..1 — the budget allocator's
 * margin lean (src/engines/allocate.ts). Client pays media plus any fee on
 * top; we pay the vendor. null when the vendor cost or retail rate is unknown,
 * so an unknown never reads as either cheap or expensive. Internal economics:
 * never reaches the model or the deck.
 */
export function grossMargin(product: Product): number | null {
  const p = product.pricing;
  const fee = p.managementFeePct ?? 0;
  const fromRatio = (retail: number | null | undefined, vendor: number | null | undefined): number | null =>
    retail != null && retail > 0 && vendor != null ? 1 - vendor / retail / (1 + fee) : null;
  switch (p.model) {
    case 'cpm':
      return fromRatio(p.retailCpm, p.vendorCpm);
    case 'cpc':
      return fromRatio(p.retailCpc, p.vendorCpc);
    case 'cpv':
      return fromRatio(p.retailCpv, p.vendorCpv);
    case 'flat':
    case 'scoped':
      return fromRatio(p.flatMonthly, p.vendorFlatMonthly);
    case 'managed':
      // Client pays media + retail fee; we pay media + vendor fee.
      return p.retailFeePct != null && p.vendorFeePct != null
        ? (p.retailFeePct - p.vendorFeePct) / (1 + p.retailFeePct)
        : null;
    default: {
      const never: never = p.model;
      throw new Error(`Unknown pricing model: ${String(never)}`);
    }
  }
}
